// src/routes/TratamientoItem.js

import {
  Outlet,
  Link,
  useLoaderData,
  useNavigate,
  NavLink,
} from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import * as API from "../services/tratamientos";

import * as Scl90API from "../services/scl90";
import * as PsqiAPI from "../services/psqi";
import * as Who5API from "../services/who5";

import { calculateScl90Results } from "./Scl90Calc";
import Scl90CardResults from "./Scl90CardResults";
import PsqiCardResults from "./PsqiCardResults";
import Who5CardResults from "./Who5CardResults";

import { scl90CardData } from "./Scl90CardData";
import { psqiCardData } from "./PsqiCardData";

import { EntmotivacionalList } from "./EntmotivacionalList";
import { AntmedicosList } from "./AntmedicosList";
import { AntpsicologicosList } from "./AntpsicologicosList";
import { ExpmedicaList } from "./ExpmedicaList";
import { DatconsumoList } from "./DatconsumoList";
import { ConspretratamientoList } from "./ConspretratamientoList";

import { VismedicasList } from "./VismedicasList";
import { VispsicologicasList } from "./VispsicologicasList";

import { PsicoterapiaList } from "./PsicoterapiaList";
import { NutricionList } from "./NutricionList";
import { MindfulnessList } from "./MindfulnessList";
import { EntrenamientoList } from "./EntrenamientoList";
import { ConspostratamientoList } from "./ConspostratamientoList";

import { InterconsultaList } from "./InterconsultaList";

import { Scl90List } from "./Scl90List";
import { IsiList } from "./IsiList";
import { PsqiList } from "./PsqiList";
import { AuditList } from "./AuditList";
import { Who5List } from "./Who5List";

import { AnaliticasList } from "./AnaliticasList";

import { FormatDate } from "../components/documentacion/FormatDate";

import { calculatePsqiResults } from "./PsqiCalc";

// Define la configuración de los gráficos de WHO-5
const who5ChartsData = [
  { title: "Puntuación Global", dataKey: "totalSumWho5" },
  // Si tienes más componentes para WHO-5, añádelos aquí
];

export async function loader({ params }) {
  const token = sessionStorage.getItem("token");

  // Si no hay token, simplemente NO llamamos a la API y retornamos null
  if (!token) {
    return null;
  }
  const { tratamientoId } = params;

  if (isNaN(Number(tratamientoId))) {
    return null;
  }

  try {
    const tratamiento = await API.getTratamientoById(tratamientoId);
    const scl90List = await Scl90API.getAllScl90ByTax(tratamientoId); // Obtener datos SCL-90
    const psqiList = await PsqiAPI.getAllPsqiByTax(tratamientoId); // Obtener datos PSQI
    const who5List = await Who5API.getAllWho5ByTax(tratamientoId); // Obtener datos WHO-5
    return { tratamiento, scl90List, psqiList, who5List };
  } catch (error) {
    console.error(
      `Error fetching tratamiento, SCL-90, PSQI o WHO-5 data: ${error}`
    );
    return null;
  }
}

const CustomNavLink = ({ text, link }) => {
  const [isActive, setIsActive] = useState(false);
  const [userInteract, setUserInteract] = useState(false);

  return (
    <NavLink
      to={link}
      onClick={() => {
        setIsActive(!isActive);
        setUserInteract(true);
      }}
      className={`toggle ${isActive ? "opened" : "closed"} ${
        userInteract ? "interact" : "no-interact"
      }`}
      // end
    >
      {text}
    </NavLink>
  );
};

export function TratamientoListItem(props) {
  const fechaFormat = FormatDate(props?.meta?.fecha);
  const [dia, mes, ano] = fechaFormat.split("/");
  const fechaFormatPaciente = `Tratamiento ${mes}/${ano} »`;
  const [userInteract, setUserInteract] = useState(false);

  return (
    <li>
      {Array.isArray(props.userRole) &&
      props.userRole.some((role) => role === "paciente") ? (
        <CustomNavLink text={fechaFormatPaciente} link={`${props.id}`} />
      ) : (
        <CustomNavLink text={fechaFormat} link={`${props.id}`} />
      )}
      <ul className="props">
        {/* Mostrar solo si no es "paciente" */}
        {Array.isArray(props.userRole) &&
          props.userRole.some((role) => role !== "paciente") && (
            <>
              <li>
                <CustomNavLink
                  text="Antecedentes »"
                  link={`${props.id}/antecedentes`}
                />
                <ul className="antecedentes">
                  <li>
                    <CustomNavLink
                      text="Antecedentes Médicos »"
                      link={`${props.id}/antecedentes/medicos`}
                    />
                    <AntmedicosList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Exploración Médica »"
                      link={`${props.id}/antecedentes/exploracion-medica`}
                    />
                    <ExpmedicaList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Antecedentes Psicológicos »"
                      link={`${props.id}/antecedentes/psicologicos`}
                    />
                    <AntpsicologicosList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Datos de Consumo »"
                      link={`${props.id}/antecedentes/datos-consumo`}
                    />
                    <DatconsumoList key={props.id} {...props} />
                  </li>
                  <li>
                    <EntmotivacionalList key={props.id} {...props} />
                  </li>

                  <li>
                    <CustomNavLink
                      text="Consulta Pre-Tratamiento »"
                      link={`${props.id}/antecedentes/consulta-pre-tratamiento`}
                    />
                    <ConspretratamientoList key={props.id} {...props} />
                  </li>
                </ul>
              </li>
              <li>
                <CustomNavLink
                  text="Hospitalización »"
                  link={`${props.id}/hospitalizacion`}
                />
                <ul className="hospitalizacion">
                  <li>
                    <CustomNavLink
                      text="Visitas Médicas »"
                      link={`${props.id}/hospitalizacion/visitas-medicas`}
                    />
                    <VismedicasList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Visitas Psicológicas »"
                      link={`${props.id}/hospitalizacion/visitas-psicologicas`}
                    />
                    <VispsicologicasList key={props.id} {...props} />
                  </li>
                </ul>
              </li>
              <li>
                <CustomNavLink
                  text="Seguimiento »"
                  link={`${props.id}/seguimiento`}
                />
                <ul className="seguimiento">
                  <li>
                    <CustomNavLink
                      text="Psicoterapia »"
                      link={`${props.id}/seguimiento/psicoterapia`}
                    />
                    <PsicoterapiaList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Nutrición »"
                      link={`${props.id}/seguimiento/nutricion`}
                    />
                    <NutricionList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Mindfulness »"
                      link={`${props.id}/seguimiento/mindfulness`}
                    />
                    <MindfulnessList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Entrenamiento »"
                      link={`${props.id}/seguimiento/entrenamiento`}
                    />
                    <EntrenamientoList key={props.id} {...props} />
                  </li>
                  <li>
                    <CustomNavLink
                      text="Consulta Post-Tratamiento »"
                      link={`${props.id}/seguimiento/consulta-post-tratamiento`}
                    />
                    <ConspostratamientoList key={props.id} {...props} />
                  </li>
                </ul>
              </li>
              <li>
                <CustomNavLink
                  text="Interconsultas »"
                  link={`${props.id}/interconsultas`}
                />
                <InterconsultaList key={props.id} {...props} />
              </li>
            </>
          )}

        {/* Mostrar siempre Cuestionarios y Analíticas */}
        <li>
          <CustomNavLink
            text="Cuestionarios »"
            link={`${props.id}/cuestionarios`}
          />
          <ul className="cuestionarios">
            <li>
              <CustomNavLink
                text={
                  Array.isArray(props.userRole) &&
                  props.userRole.includes("paciente")
                    ? "Síntomas"
                    : "SCL-90-R »"
                }
                link={`${props.id}/cuestionarios/scl-90`}
              />
              {Array.isArray(props.userRole) &&
                props.userRole.some((role) => role !== "paciente") && (
                  <Scl90List key={props.id} {...props} />
                )}
            </li>
            <li>
              <CustomNavLink
                text={
                  Array.isArray(props.userRole) &&
                  props.userRole.includes("paciente")
                    ? "Sueño"
                    : "PSQI »"
                }
                link={`${props.id}/cuestionarios/psqi`}
              />
              {Array.isArray(props.userRole) &&
                props.userRole.some((role) => role !== "paciente") && (
                  <PsqiList key={props.id} {...props} />
                )}
            </li>
            {/* Mostrar Audit solo si no es "paciente" */}
            {Array.isArray(props.userRole) &&
              props.userRole.some((role) => role !== "paciente") && (
                <li>
                  <CustomNavLink
                    text="A.U.D.I.T »"
                    link={`${props.id}/cuestionarios/audit`}
                  />
                  <AuditList key={props.id} {...props} />
                </li>
              )}
            <li>
              <CustomNavLink
                text={
                  Array.isArray(props.userRole) &&
                  props.userRole.includes("paciente")
                    ? "Bienestar"
                    : "WHO-5 »"
                }
                link={`${props.id}/cuestionarios/who5`}
              />
              {Array.isArray(props.userRole) &&
                props.userRole.some((role) => role !== "paciente") && (
                  <Who5List key={props.id} {...props} />
                )}
            </li>
          </ul>
        </li>

        <li>
          <CustomNavLink text="Analíticas" link={`${props.id}/analiticas`} />
          {Array.isArray(props.userRole) &&
            props.userRole.some((role) => role !== "paciente") && (
              <AnaliticasList key={props.id} {...props} />
            )}
        </li>

        {/* Mostrar Mejórate solo si es "paciente" */}
        {Array.isArray(props.userRole) &&
          props.userRole.includes("paciente") && (
            <li>
              <CustomNavLink text="Mejórate" link={`${props.id}/mejorate`} />
            </li>
          )}

        {/* Mostrar Documentación solo si no es "paciente" */}
        {Array.isArray(props.userRole) &&
          props.userRole.some((role) => role !== "paciente") && (
            <li>
              <CustomNavLink
                text="Documentación"
                link={`${props.id}/documentacion`}
              />
            </li>
          )}
      </ul>
    </li>
  );
}

export function TratamientoItemContent(props) {
  const { tratamiento, scl90List, psqiList, who5List } = useLoaderData();

  console.log("UserRole:", props.userRole);

  const pacienteMeta = tratamiento.pacienteMeta;
  const sexPaciente = pacienteMeta?.meta?.sexo_paciente || "hombre";

  const navigate = useNavigate();

  const isPaciente = Array.isArray(props.userRole) && props.userRole.includes("paciente");

  // Procesar y transformar los datos SCL-90
  const renderEmptyScl90Message = () => {
    if (!scl90List || scl90List.length === 0) {

      return (
        <h3>
          Aquí podrás ver la evolución de tus resultados de{" "}
          {isPaciente ? "sueño" : "SCL-90"}. Actualmente no existen datos para
          comparar.
        </h3>
      );
    }
    return null;
  };

  useEffect(() => {
    if (
      scl90List.length === 1 &&
      Array.isArray(tratamiento.userRole) &&
      tratamiento.userRole.includes("paciente")
    ) {
      const objectId = scl90List[0].id;
      navigate(`${objectId}`);
    }
  }, [scl90List, navigate, tratamiento.userRole]);

  const sortedScl90List = useMemo(() => {
    return [...scl90List].sort((a, b) => {
      const dateA = new Date(a.meta.metaFecha);
      const dateB = new Date(b.meta.metaFecha);
      return dateA - dateB;
    });
  }, [scl90List]);

  const countResponsesScl90 = (responses) => {
    return Object.keys(responses).filter(
      (key) => key.startsWith("scl90Preg") && responses[key] !== ""
    ).length;
  };

  // Definir la función de formateo de fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const transformedScl90List = useMemo(() => {
    return sortedScl90List
      .map((scl90) => {
        const responses = scl90.meta;
        const responseCount = countResponsesScl90(responses);

        if (responseCount < 81) {
          return null;
        }

        const results = calculateScl90Results(responses);

        return {
          fecha: formatDate(scl90.meta.metaFecha),
          ...results,
          responseCount,
        };
      })
      .filter((item) => item !== null);
  }, [sortedScl90List]);

  const percSex = useMemo(
    () => ({
      percSom: sexPaciente === "hombre" ? "percSomHomb" : "percSomMuj",
      percObs: sexPaciente === "hombre" ? "percObsHomb" : "percObsMuj",
      percSi: sexPaciente === "hombre" ? "percSiHomb" : "percSiMuj",
      percDep: sexPaciente === "hombre" ? "percDepHomb" : "percDepMuj",
      percAns: sexPaciente === "hombre" ? "percAnsHomb" : "percAnsMuj",
      percHos: sexPaciente === "hombre" ? "percHosHomb" : "percHosMuj",
      percFob: sexPaciente === "hombre" ? "percFobHomb" : "percFobMuj",
      percPar: sexPaciente === "hombre" ? "percParHomb" : "percParMuj",
      percPsic: sexPaciente === "hombre" ? "percPsicHomb" : "percPsicMuj",
      percGsi: sexPaciente === "hombre" ? "percGsiHomb" : "percGsiMuj",
      percPst: sexPaciente === "hombre" ? "percPstHomb" : "percPstMuj",
      percPsdi: sexPaciente === "hombre" ? "percPsdiHomb" : "percPsdiMuj",
      totalDimenAdi: "totalDimenAdi",
    }),
    [sexPaciente]
  );

  // Procesar y transformar los datos PSQI
  const transformPsqiData = (psqiData) => {
    return psqiData
      .map((psqi) => {
        // Llama a calculatePsqiResults para obtener los cálculos
        const results = calculatePsqiResults(psqi.meta);

        // Formatear la fecha a dd/mm/aaaa
        const rawFecha = psqi.meta?.metaFecha;
        const [year, month, day] = rawFecha.split("-");
        const fecha = `${day}/${month}/${year}`;

        return {
          ...results,
          fecha,
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.fecha.split("/").reverse().join("-"));
        const dateB = new Date(b.fecha.split("/").reverse().join("-"));
        return dateA - dateB; // Ordena de más antiguo a más reciente
      });
  };

  const transformedPsqiList = useMemo(
    () => transformPsqiData(psqiList),
    [psqiList]
  );
  console.log("Datos filtrados para gráficos PSQI:", transformedPsqiList);

  const renderEmptyPsqiMessage = () => {
    if (psqiList.length === 0) {
      return (
        <h3>
          Aquí podrás ver la evolución de tus resultados de{" "}
          {isPaciente ? "síntomas" : "PSQI"}. Actualmente no existen datos para
          comparar.
        </h3>
      );
    }

    if (transformedPsqiList.length === 0) {
      return (
        <h3>
          No hay suficientes respuestas para mostrar los gráficos de{" "}
          {isPaciente ? "síntomas" : "PSQI"}. Por favor, completa más
          cuestionarios.
        </h3>
      );
    }

    return null;
  };

  useEffect(() => {
    if (
      psqiList.length === 1 &&
      Array.isArray(tratamiento.userRole) &&
      tratamiento.userRole.includes("paciente")
    ) {
      const objectId = psqiList[0].id; // Obtén el ID del único objeto
      navigate(`${objectId}`); // Navega a la URL con el ID
    }
  }, [psqiList, navigate, tratamiento.userRole]);

  // Procesar y transformar los datos WHO-5
  const transformWho5Data = (who5Data) => {
    return who5Data
      .map((who5) => {
        const who5Alegre = who5.meta?.who5Alegre;
        const who5Tranquilo = who5.meta?.who5Tranquilo;
        const who5Activo = who5.meta?.who5Activo;
        const who5Despejado = who5.meta?.who5Despejado;
        const who5Interesan = who5.meta?.who5Interesan;

        // Suma total de WHO-5
        const totalSumWho5 =
          4 *
          ((parseInt(who5Alegre) || 0) +
            (parseInt(who5Tranquilo) || 0) +
            (parseInt(who5Activo) || 0) +
            (parseInt(who5Despejado) || 0) +
            (parseInt(who5Interesan) || 0));

        // Formatear la fecha a dd/mm/aaaa
        const rawFecha = who5.meta?.metaFecha;
        const [year, month, day] = rawFecha.split("-");
        const fecha = `${day}/${month}/${year}`;

        return {
          fecha,
          totalSumWho5,
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.fecha.split("/").reverse().join("-"));
        const dateB = new Date(b.fecha.split("/").reverse().join("-"));
        return dateA - dateB; // Ordena de más antiguo a más reciente
      });
  };

  const transformedWho5List = useMemo(
    () => transformWho5Data(who5List),
    [who5List]
  );
  console.log("Datos filtrados para gráficos WHO-5:", transformedWho5List);

  const renderEmptyWho5Message = () => {
    if (who5List.length === 0) {
      return (
        <h3>
          Aquí podrás ver la evolución de tus resultados de {isPaciente ? "bienestar" : "WHO-5"}. Actualmente
          no existen datos para comparar.
        </h3>
      );
    }

    if (transformedWho5List.length === 0) {
      return (
        <h3>
          No hay suficientes respuestas para mostrar los gráficos de {isPaciente ? "bienestar" : "WHO-5"}. Por
          favor, completa más cuestionarios.
        </h3>
      );
    }

    return null;
  };

  useEffect(() => {
    if (
      who5List.length === 1 &&
      Array.isArray(tratamiento.userRole) &&
      tratamiento.userRole.includes("paciente")
    ) {
      const objectId = who5List[0].id; // Obtén el ID del único objeto
      navigate(`${objectId}`); // Navega a la URL con el ID
    }
  }, [who5List, navigate, tratamiento.userRole]);

  return (
    <>
      <div
        id="content"
        className="comparativa-graficas-container tratamiento-item-page"
      >
        <h2>Resumen de Cuestionarios</h2>

        {/* Sección de resultados de SCL-90 */}
        {renderEmptyScl90Message() || (
          <form className="analitic-form">
            <h3>Síntomas</h3>
            <div className="five-col-analitic">
              {scl90CardData.map((scl90Data) => {
                const scl90Key = percSex[scl90Data.dataKey];
                if (
                  !scl90Key ||
                  !transformedScl90List.some((item) => scl90Key in item)
                ) {
                  console.warn(
                    `Data key "${scl90Key}" is missing in transformedScl90List`
                  );
                  return null;
                }
                return (
                  <Scl90CardResults
                    key={scl90Data.dataKey}
                    title={scl90Data.title}
                    tooltipText={scl90Data.tooltipText}
                    dataKey={scl90Key}
                    transformedScl90List={transformedScl90List}
                    percSex={percSex}
                    height={240}
                  />
                );
              })}
            </div>
          </form>
        )}

        {/* Sección de resultados de PSQI */}
        {renderEmptyPsqiMessage() || (
          <form className="analitic-form">
            <h3>Sueño</h3>
            <div className="five-col-analitic">
              {psqiCardData.map((psqiData) => (
                <PsqiCardResults
                  key={psqiData.dataKey}
                  title={psqiData.title}
                  data={transformedPsqiList}
                  dataKey={psqiData.dataKey}
                  height={240}
                />
              ))}
            </div>
          </form>
        )}

        {/* Sección de resultados de WHO-5 */}
        {renderEmptyWho5Message() || (
          <form className="analitic-form">
            <h3>Bienestar</h3>
            <div className="five-col-analitic">
              {who5ChartsData.map((who5Data) => (
                <Who5CardResults
                  key={who5Data.dataKey}
                  title={who5Data.title}
                  transformedWho5List={transformedWho5List}
                  dataKey={who5Data.dataKey}
                  height={240}
                />
              ))}
            </div>
          </form>
        )}
      </div>
    </>
  );
}
